import React from 'react';
import styled from 'styled-components';

import { Switcher, SwitcherWrapper } from './Switcher';
import { AccountsGetJwtData, GlobalNavState, Service, ToSInfo, UserInfo } from '../types';

import { BOXSHADOWS, BREAKPOINTS, COLORS, MobileSwitcherMenu, Z_INDEXES } from '@laerdal/life-react-components';
import FocusTrap from 'focus-trap-react';
import i18next from '../i18n';
import { getVSimService, mapToMenuItem, mapToMenuItems, rewriteForMarket } from './helpers';
import { ChangeOrganizationModal } from './ChangeOrganization/modals/ChangeOrganizationModal';
import { ChangeOrganizationMobileView } from './ChangeOrganization/ChangeOrganizationMobileView';
import EventEmitter from '../utility/Events/EventEmitter';
import { ConnectApi, ApiHelpers, OrgDirApi } from '../utility';
import { Helmet } from 'react-helmet';
import JwtDecode from 'jwt-decode';

export interface GlobalNavProps {}

export const SwitcherContainer = styled.div`
  display: none;

  position: fixed !important;
  height: 100% !important;
  top: 0;
  right: 0;
  z-index: ${Z_INDEXES.off_canvas};

  &.visible {
    display: flex;
  }

  .DesktopSwitcher {
    display: none;

    ${BREAKPOINTS.MEDIUM} {
      display: flex;
    }
  }

  .MobileDesktopSwitcher {
    display: flex;

    ${BREAKPOINTS.MEDIUM} {
      display: none;
    }
  }
`;

export const DesktopSwitcherContainer = styled.div`
  display: flex;
  position: absolute;
  top: 0;
  bottom: 0;
  right: 0;
  width: 0;
  overflow-x: hidden;
  box-shadow: none;

  transition: width 0.3s, box-shadow 0.3s;

  &.visible {
    ${BREAKPOINTS.MEDIUM} {
      box-shadow: ${BOXSHADOWS.BOXSHADOW_CENTERED};
      width: 416px;
      transition: width 0.3s;
    }
  }
`;

export const Darkness = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: rgba(0, 0, 0, 0.3);
  opacity: 0;
  visibility: hidden;

  transition: visibility 0.6s, opacity 0.6s;

  &.visible {
    visibility: visible;
    opacity: 1;
    position: fixed;
  }
`;

const MobileMenuWrapper = styled.div`
  background-color: ${COLORS.white};
  position: absolute;
  right: 0;
  top: 0;
  z-index: ${Z_INDEXES.off_canvas};
  width: 360px;
  height: 100%;
  display: none;
  flex-direction: column;

  &:focus-within,
  &:hover,
  &.visible {
    background: ${COLORS.white};
  }
`;

const OverlayWrapper = styled.div`
  z-index: ${Z_INDEXES.backdrop};
  position: absolute;
`;

interface Options {
  env?: string;
  token?: string;
  locale?: string;
  canSwitchOrganization?: boolean;
  serviceId?: string;
}

export class GlobalNav extends React.Component<GlobalNavProps, GlobalNavState> {
  container: HTMLElement | undefined;
  emitter: EventEmitter;
  private static firstRun = true;

  constructor(props: GlobalNavProps) {
    super(props);
    this.state = { loggedIn: false, visible: false, showChangeOrganizationModal: false, menuState: 'default' };
    this.emitter = new EventEmitter();
  }

  componentDidUpdate(_: GlobalNavProps, prevState: GlobalNavState) {
    //if user logged in and has portal access, we should check for vsim access
    if (this.state.loggedIn && this.state.organization?.customerNo && this.state.organization?.customerNo !== prevState.organization?.customerNo) {
      this.checkShouldAddVsimService();
      this.redirectToToSPage();
    }
    //if token has been renewed call getUserInfo
    if (this.state.token !== prevState.token) {
      this.getUserInfo();
    }
  }

  setLocale(locale: string | undefined) {
    if (i18next.language !== locale && locale) {
      i18next.changeLanguage(locale.toLowerCase()).then(() => {
        const root = document.getElementById('switcherRoot');
        if (root) {
          root.lang = locale;
        }
        this.setState({ locale: locale });
      });
    }
  }

  initialize(options: Options | undefined) {
    this.setState({
      env: options?.env,
      token: options?.token,
      canSwitchOrganization: options?.canSwitchOrganization || false,
      serviceId: options?.serviceId,
    });

    this.hideToggler();

    if (this.state.token) {
      this.getUserInfo();
      this.setLocale(options?.locale);
      // @ts-ignore
    } else if (window && window.gigya && window.gigya.accounts) {
      // @ts-ignore
      window.gigya.accounts.addEventHandlers({
        onLogin: this.onLoginHandler,
        onLogout: this.onLogoutHandler,
      });

      this.validate();
    }
  }

  onLoginHandler(eventObj: any) {
    window.globalNav.getJWT();

    window.globalNav.setState({
      loggedIn: true,
      email: eventObj.profile.email,
      firstName: eventObj.profile.firstName,
      lastName: eventObj.profile.lastName,
    });

    this.setLocale(eventObj?.profile.locale);
  }

  onLogoutHandler() {
    window.globalNav.hide();
    window.globalNav.hideToggler();

    if (window.globalNav.state && window.globalNav.state.loggedIn) {
      window.globalNav.setState({
        loggedIn: false,
        firstName: '',
        lastName: '',
        email: '',
        token: '',
        organization: undefined,
        availableOrganizations: undefined,
        services: undefined,
      });
    }
  }

  validate() {
    // @ts-ignore
    window.gigya.accounts.getAccountInfo({
      callback: (eventObj: any) => {
        if (eventObj.errorCode === 0) {
          window.globalNav.setState({
            loggedIn: true,
            email: eventObj.profile.email,
            firstName: eventObj.profile.firstName,
            lastName: eventObj.profile.lastName,
          });
          this.setLocale(eventObj.profile.locale);
          this.getJWT();
        } else if (eventObj.errorCode === 403005) {
          window.globalNav.setState({
            loggedIn: false,
            email: '',
            firstName: '',
            lastName: '',
          });
        } else {
          console.error(eventObj.errorCode);
        }
      },
      extraProfileFields: 'locale',
    });
  }

  redirectToToSPage() {
    if (this.state.token && this.state.serviceId) {
      let portalBaseUrl = ApiHelpers.getOrgDirUrl(this.state?.env);
      let serviceId = this.state.serviceId;
      OrgDirApi.GetPendingConsentForService(portalBaseUrl, this.state.token, serviceId)
        .then((response) => {
          let tosInfo: ToSInfo = response.data;
          const storageKey = `tos_redirect_${tosInfo.termsOfServiceVersionId}_${tosInfo.onBehalfOfOrganizationId}`;
          const item = localStorage.getItem(storageKey);
          if (tosInfo.canApprove && !item) {
            localStorage.setItem(storageKey, 'redirected');
            window.location.href = `${ApiHelpers.getConnectUrl(this.state?.env)}/terms-of-service/${serviceId}?_redirectUrl=${encodeURI(window.location.href)}`;
          }
        })
        .catch((error) => {
          console.error(error);
        });
    }
  }

  getUserInfo() {
    if (this.state.token) {
      const jwt: any = JwtDecode(this.state.token);
      //take a 2 minutes margin for token renewal
      //such as we ensure that token is not expired in between this moment and calling Connect
      if (Date.now() >= (jwt.exp - 120) * 1000) {
        this.getJWT();
        return;
      }

      let portalBaseUrl = ApiHelpers.getConnectUrl(this.state?.env);
      ConnectApi.GetCurrentUserInfo(portalBaseUrl, this.state.token)
        .then((response) => {
          let userInfo: UserInfo = response;
          let services = Object.assign([], userInfo.services) as Service[];

          if (this.state.vSimServiceUrl) {
            services.push(getVSimService(this.state.vSimServiceUrl));
          }

          services = services.sort((a, b) => {
            const tmp = Math.abs(a.switcherOrdering) - Math.abs(b.switcherOrdering);
            if (tmp) return tmp;

            return a.name.localeCompare(b.name);
          });

          let tmp = mapToMenuItems(services);

          userInfo.market && tmp.forEach((a) => (a.to = rewriteForMarket(a.to, userInfo?.market)));

          this.setState({
            services: tmp,
            organization: userInfo.currentOrganization,
            availableOrganizations: userInfo.organizations,
            market: userInfo.market,
          });
          if (!!userInfo.currentOrganization) {
            this.showToggler();
          }

          // For first run, repeat userInfo request after 15 seconds then every 30 seconds
          const interval = GlobalNav.firstRun ? 15000 : 30000;
          GlobalNav.firstRun = false;
          setTimeout(() => {
            this.getUserInfo();
          }, interval);
        })
        .catch((error) => {
          this.setState({  });
          console.error('GlobalNav error while retrieving current user info', error);
        });
    }
  }

  checkShouldAddVsimService() {
    if (this.state.token && this.state.organization) {
      ConnectApi.HasVismAccess(ApiHelpers.getConnectUrl(this.state?.env), this.state.token, this.state.organization?.customerNo).then((vSimServiceUrl) => {
        if (vSimServiceUrl) {
          const servicesToUpdate = Object.assign([], this.state.services || []);
          servicesToUpdate.push(mapToMenuItem(getVSimService(vSimServiceUrl)));
          this.setState({ services: servicesToUpdate, vSimServiceUrl });
        }
      });
    }
  }

  getJWT() {
    // @ts-ignore
    window.gigya.accounts.getJWT({
      fields: 'firstName, lastName, email',
      expiration: 3600,
      callback: (res: AccountsGetJwtData) => {
        if (res.errorCode === 0) {
          window.globalNav.setState({
            token: res.id_token,
          });
        } else if (res.errorCode === 403005) {
          console.error('GlobalNav error: Unauthorized user trying to retrieve token');
          // Unauthorized user
          window.globalNav.setState({
            token: undefined,
          });
        } else {
          console.error('GlobalNav error:', res.errorCode);
        }
      },
    });
  }

  hide() {
    if (!!this.state.organization) {
      this.setState({ visible: false });
      document.querySelector('body')?.classList.remove('switcher-visible');
    }
  }

  show() {
    if (!!this.state.organization) {
      this.setState({ visible: true });
      document.querySelector('body')?.classList.add('switcher-visible');
      const toggler = document.getElementById('globalNavAppsButton') ?? document.querySelector('.globalNav-toggler');
      if (toggler === document?.activeElement) {
        // @ts-ignore
        document.querySelector(`${SwitcherWrapper} a:first-of-type`)?.focus();
      }
    }
  }

  toggle() {
    if (!this.state.visible) {
      this.show();
    } else {
      this.hide();
    }
  }

  showToggler() {
    this.setState({ hidden: false });
  }

  hideToggler() {
    this.setState({ hidden: true });
  }

  setCanSwitchOrganization(value: boolean) {
    this.setState({ canSwitchOrganization: value });
  }

  onChangeCurrentOrganization(organizationId: string) {
    const nextCurrentOrg = this.state.availableOrganizations?.find((a) => a.id === organizationId);
    if (this.state.token && nextCurrentOrg) {
      let orgDirBaseUrl = ApiHelpers.getOrgDirUrl(this.state?.env);
      const portalBaseUrl = ApiHelpers.getConnectUrl(this.state?.env);
      const token = this.state.token;
      return OrgDirApi.ChangeCurrentOrganization(orgDirBaseUrl, this.state.token, organizationId)
        .then(() => {
          this.setState({ organization: nextCurrentOrg, showChangeOrganizationModal: false });
          //trigger get user info in connect to force ecl invalidate the cache
          //otherwise, ECl will display the data for the previous current org
          ConnectApi.GetCurrentUserInfo(portalBaseUrl, token, true, true);
          this.emitter.emit('notifyOrganizationChanged', { entityId: organizationId });
        })
        .catch((error) => {
          console.error(error);
        });
    }
    return Promise.resolve();
  }

  toggleChangeOrganizationView(isMobileView: boolean = false) {
    if (!this.state.canSwitchOrganization || !this.state.organization) {
      return;
    }
    isMobileView ? this.setState({ menuState: 'list-organizations' }) : this.setState({ showChangeOrganizationModal: !this.state.showChangeOrganizationModal });
  }

  private getCurrentStateMobileContent() {
    return this.state.menuState === 'list-organizations' ? (
      <ChangeOrganizationMobileView
        i18next={i18next}
        selectedOrgId={this.state.organization?.id}
        availableOrganizations={this.state.availableOrganizations}
        goBack={() => {
          this.setState({ menuState: 'default' });
        }}
        handleChangeOrganization={(orgId) => this.onChangeCurrentOrganization(orgId)}
      />
    ) : (
      <MobileSwitcherMenu
        footerAction={
          this.state.canSwitchOrganization && (this.state.availableOrganizations ?? []).length > 1
            ? { label: i18next.t('Change organization'), action: () => this.toggleChangeOrganizationView(true) }
            : undefined
        }
        label={i18next.t('Switch site')}
        header={this.state.organization?.name || ''}
        note={i18next.t('As a member of this organization, you can use the apps and websites listed below.')}
        onMenuClose={() => this.hide()}
        items={this.state.services}
      />
    );
  }

  render() {
    return (
      <>
        {this.state.hidden && (
          <Helmet>
            <style>{'.globalNav-toggler, #globalNavAppsButton {display: none;}'}</style>
          </Helmet>
        )}
        {!this.state.hidden && (
          <Helmet>
            <style>{'.globalNav-toggler, #globalNavAppsButton {display: block;}'}</style>
          </Helmet>
        )}
        <FocusTrap active={this.state.visible && !this.state.showChangeOrganizationModal} focusTrapOptions={{ initialFocus: false }}>
          <OverlayWrapper>
            <Darkness
              className={this.state.visible ? 'visible' : ''}
              onClick={() => {
                this.hide();
              }}></Darkness>
            <SwitcherContainer className={this.state.visible ? 'visible' : ''}>
              <DesktopSwitcherContainer
                onClick={(e: any) => {
                  e.preventDefault();
                }}
                className={'DesktopSwitcherContainer'.concat(this.state.visible ? ' visible' : '')}>
                <div className="DesktopSwitcher">
                  <Switcher
                    i18n={i18next}
                    loggedIn={this.state.loggedIn}
                    firstName={this.state.firstName}
                    lastName={this.state.lastName}
                    email={this.state.email}
                    organization={this.state.organization}
                    token={this.state.token}
                    services={this.state.services}
                    hide={() => this.hide()}
                    canSwitchCurrentOrganization={!!this.state.canSwitchOrganization && (this.state.availableOrganizations ?? []).length > 1}
                    onShowChangeOrganizationModal={() => this.toggleChangeOrganizationView()}
                  />
                </div>
              </DesktopSwitcherContainer>
              <MobileMenuWrapper className={'MobileDesktopSwitcher'.concat(this.state.visible ? ' visible' : '')}>
                <>{this.getCurrentStateMobileContent()}</>
              </MobileMenuWrapper>
            </SwitcherContainer>
          </OverlayWrapper>
        </FocusTrap>
        <ChangeOrganizationModal
          i18next={i18next}
          selected={this.state.organization}
          availableOrganizations={this.state.availableOrganizations ?? []}
          isModalOpen={this.state.showChangeOrganizationModal}
          closeModal={() => this.toggleChangeOrganizationView()}
          onChangeOrganization={(orgId) => this.onChangeCurrentOrganization(orgId)}
        />
      </>
    );
  }
}
